// Integrations.jsx — AI Assistant / MCP server management.
// Operators generate Personal Access Tokens here, then paste the token into
// Claude Desktop / Cursor / ChatGPT so those assistants can drive BidForge
// on their behalf.  The raw token is only shown ONCE at creation.
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Copy, Key, Plus, Trash2, ShieldCheck, ExternalLink, Check, Terminal, Sparkles, Wand2 } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function Integrations() {
  const [tokens, setTokens] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState(["*"]);
  const [freshToken, setFreshToken] = useState(null); // shown-once raw token
  const [copied, setCopied] = useState(null);

  const AVAILABLE_SCOPES = [
    { id: "*", label: "Full workspace access (recommended for personal use)", default: true },
    { id: "estimates:read", label: "estimates:read — list + retrieve estimates" },
    { id: "estimates:write", label: "estimates:write — create + update estimates" },
    { id: "clients:read", label: "clients:read — list + retrieve clients" },
    { id: "clients:write", label: "clients:write — create clients" },
    { id: "pricing:read", label: "pricing:read — read the workspace price book" },
    { id: "proposals:read", label: "proposals:read — retrieve proposal narrative + section-fill status" },
    { id: "proposals:write", label: "proposals:write — patch section content + regenerate SOV" },
    { id: "proposals:export", label: "proposals:export — server-render .docx / .pdf / binder downloads" },
    { id: "takeoffs:read", label: "takeoffs:read — list measured takeoff quantities on an opportunity" },
  ];

  // Curated scope preset for Argus (BidForge's BD assistant persona). Gives
  // Argus the full read → write → export chain for proposals, read-only
  // access to takeoffs (so it doesn't re-derive quantities), plus BD-pipeline
  // read/write on estimates and read-only clients + pricing. Withholds
  // `clients:write` — Argus should propose, the operator confirms new client
  // creation via UI. Every scope here is granular, none is `*`.
  const ARGUS_PRESET_SCOPES = [
    "estimates:read",
    "estimates:write",
    "clients:read",
    "pricing:read",
    "proposals:read",
    "proposals:write",
    "proposals:export",
    "takeoffs:read",
  ];

  const toggleScope = (id) => {
    if (id === "*") { setSelectedScopes(["*"]); return; }
    setSelectedScopes((cur) => {
      const without = cur.filter((s) => s !== "*");
      return without.includes(id) ? without.filter((s) => s !== id) : [...without, id];
    });
  };

  const load = async () => {
    try {
      const [t, m] = await Promise.all([
        api.get("/pat/list"),
        api.get("/mcp/metadata"),
      ]);
      setTokens(t.data.tokens || []);
      setTools(m.data.tools || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load integrations");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) { toast.error("Give the token a name (e.g. 'Claude Desktop — Micah')"); return; }
    if (selectedScopes.length === 0) { toast.error("Select at least one scope"); return; }
    setCreating(true);
    try {
      const r = await api.post("/pat/create", { name: newName.trim(), scopes: selectedScopes });
      setFreshToken(r.data);
      setNewName("");
      setSelectedScopes(["*"]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create token");
    } finally { setCreating(false); }
  };

  // One-click: mint an Argus-ready token with the curated preset scope set.
  // Uses a distinctive default name so the operator can find it later in
  // the token list without confusing it with a Claude Desktop / Cursor PAT.
  const createArgusToken = async () => {
    setCreating(true);
    try {
      const suffix = new Date().toISOString().slice(0, 10);
      const argusName = `Argus — ${suffix}`;
      const r = await api.post("/pat/create", { name: argusName, scopes: ARGUS_PRESET_SCOPES });
      setFreshToken(r.data);
      setSelectedScopes(["*"]);
      setNewName("");
      await load();
      toast.success("Argus token minted with the recommended scopes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create Argus token");
    } finally { setCreating(false); }
  };

  const revoke = async (patId, name) => {
    if (!window.confirm(`Revoke "${name}"? Any AI assistant using it will lose access immediately.`)) return;
    try {
      await api.del(`/pat/${patId}`);
      toast.success("Token revoked");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://bidforgeai.org";
  const mcpUrl = `${backendUrl}/api/mcp/v1`;
  const jarvisUrl = `${mcpUrl}?token=${freshToken?.token || "<PASTE-YOUR-TOKEN-HERE>"}`;

  const claudeConfig = useMemo(() => JSON.stringify({
    mcpServers: {
      bidforge: {
        command: "npx",
        args: [
          "-y", "mcp-remote", mcpUrl,
          "--header", `Authorization: Bearer ${freshToken?.token || "<PASTE-YOUR-TOKEN-HERE>"}`,
        ],
      },
    },
  }, null, 2), [mcpUrl, freshToken?.token]);

  const cursorConfig = useMemo(() => JSON.stringify({
    "mcp.servers": {
      bidforge: {
        url: mcpUrl,
        headers: { Authorization: `Bearer ${freshToken?.token || "<PASTE-YOUR-TOKEN-HERE>"}` },
      },
    },
  }, null, 2), [mcpUrl, freshToken?.token]);

  const copy = async (label, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(label); setTimeout(() => setCopied(null), 1500); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="integrations-page">
      <div className="mono-label text-fuchsia-400 mb-2">// AI ASSISTANT</div>
      <h1 className="font-display font-black text-3xl tracking-tight mb-2 flex items-center gap-3">
        <Bot className="w-8 h-8 text-fuchsia-400" />
        Connect BidForge to Your AI Assistant
      </h1>
      <p className="text-slate-400 max-w-2xl mb-8">
        BidForge speaks the <a className="text-fuchsia-300 underline" href="https://modelcontextprotocol.io" target="_blank" rel="noreferrer">Model Context Protocol <ExternalLink className="w-3 h-3 inline" /></a> so Claude Desktop, Cursor, ChatGPT, or any MCP-compatible assistant can read your pipeline, search opportunities, and trigger AI actions on your behalf. Generate a token below, then paste it into your assistant's config.
      </p>

      {/* Fresh-token panel (shown right after creation) */}
      {freshToken && (
        <div className="mb-8 border-2 border-fuchsia-400 bg-fuchsia-500/5 p-5 rounded" data-testid="fresh-token-panel">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-5 h-5 text-fuchsia-300" />
            <div className="font-bold text-fuchsia-200">Your new token — copy it now.</div>
          </div>
          <div className="text-sm text-slate-300 mb-3">
            This is the only time you'll see the full token. If you lose it, revoke and mint a new one.
          </div>
          <div className="flex items-center gap-2 bg-black/50 border border-fuchsia-500/40 p-3 font-mono text-sm break-all">
            <code className="flex-1 text-fuchsia-200" data-testid="fresh-token-value">{freshToken.token}</code>
            <button
              onClick={() => copy("token", freshToken.token)}
              className="p-2 hover:bg-fuchsia-500/20 rounded flex-shrink-0"
              data-testid="copy-token-btn"
            >
              {copied === "token" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-fuchsia-300" />}
            </button>
          </div>
          <button
            onClick={() => setFreshToken(null)}
            className="mt-3 text-xs text-slate-400 hover:text-white"
            data-testid="dismiss-fresh-token"
          >
            I've saved it — dismiss this panel
          </button>
        </div>
      )}

      {/* Create + list tokens */}
      <div className="mb-10 bg-[#0D1424] border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Key className="w-5 h-5 text-cyan-400" />
          <div className="font-display font-bold text-lg">Personal Access Tokens</div>
        </div>

        {/* Argus one-click preset — mints a curated token in a single click */}
        <div
          className="mb-5 relative overflow-hidden border border-cyan-400/40 bg-gradient-to-r from-cyan-400/[0.06] via-cyan-400/[0.03] to-transparent p-4"
          data-testid="argus-preset-panel"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex-shrink-0 border border-cyan-400/40 flex items-center justify-center text-cyan-400">
              <Wand2 className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="mono-label text-cyan-400 text-[11px]">ONE-CLICK RECIPE</div>
                <div className="mono-label text-slate-500 text-[10px]">·</div>
                <div className="mono-label text-slate-400 text-[11px]">RECOMMENDED FOR ARGUS</div>
              </div>
              <div className="font-display font-bold text-white text-base">
                Set up a token for Argus
              </div>
              <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                Mints a granular token with the exact scopes Argus needs to complete the read → write → export chain — no more, no less. Withholds <code className="text-fuchsia-300 font-mono">clients:write</code> so Argus can propose new clients but you confirm.
              </p>
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="argus-preset-scopes">
                {ARGUS_PRESET_SCOPES.map((s) => (
                  <code
                    key={s}
                    className="text-[10px] px-1.5 py-0.5 border border-cyan-400/30 text-cyan-300 font-mono"
                  >
                    {s}
                  </code>
                ))}
              </div>
            </div>
            <button
              onClick={createArgusToken}
              disabled={creating}
              className="flex-shrink-0 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed text-[#070B14] px-5 py-2.5 mono-label flex items-center gap-2 self-center whitespace-nowrap"
              data-testid="argus-preset-generate-btn"
            >
              <Wand2 className="w-4 h-4" />
              {creating ? "MINTING…" : "GENERATE FOR ARGUS"}
            </button>
          </div>
        </div>

        <div className="flex gap-3 mb-4">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Token name — e.g. Claude Desktop / MacBook Pro"
            className="flex-1 bg-black/40 border border-white/10 focus:border-fuchsia-400 outline-none px-3 py-2 text-sm text-white placeholder-slate-500"
            onKeyDown={(e) => { if (e.key === "Enter") create(); }}
            data-testid="new-token-name-input"
          />
          <button
            onClick={create}
            disabled={creating || !newName.trim()}
            className="bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2 mono-label flex items-center gap-2"
            data-testid="create-token-btn"
          >
            <Plus className="w-4 h-4" />
            GENERATE
          </button>
        </div>

        {/* Scope selector */}
        <div className="mb-6 border border-white/5 bg-black/20 p-3" data-testid="scope-selector">
          <div className="mono-label text-slate-400 text-[11px] mb-2">SCOPES — what this token can do</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {AVAILABLE_SCOPES.map((s) => (
              <label
                key={s.id}
                className="flex items-start gap-2 text-xs text-slate-300 hover:text-white cursor-pointer p-1.5 hover:bg-white/5 rounded"
                data-testid={`scope-${s.id.replace(":", "-").replace("*", "all")}`}
              >
                <input
                  type="checkbox"
                  checked={selectedScopes.includes(s.id)}
                  onChange={() => toggleScope(s.id)}
                  className="mt-0.5 accent-fuchsia-500"
                />
                <span>
                  <code className="text-fuchsia-300 font-mono">{s.id}</code>
                  <span className="text-slate-400"> — {s.label.split("—")[1] || s.label}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : tokens.length === 0 ? (
          <div className="text-slate-500 text-sm border border-dashed border-white/10 p-4 text-center">
            No tokens yet. Generate one above to connect your first AI assistant.
          </div>
        ) : (
          <div className="space-y-2" data-testid="token-list">
            {tokens.map((t) => (
              <div
                key={t.pat_id}
                className={`flex items-center gap-3 border p-3 ${t.revoked ? "border-white/5 opacity-40" : "border-white/10 hover:border-white/20"}`}
                data-testid={`token-row-${t.pat_id}`}
              >
                <Key className={`w-4 h-4 ${t.revoked ? "text-slate-600" : "text-cyan-400"} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-white truncate">{t.name}</div>
                    {t.revoked && <span className="mono-label text-[10px] text-slate-500">REVOKED</span>}
                  </div>
                  <div className="text-xs text-slate-500 font-mono">
                    {t.prefix}…  ·  created {fmtDate(t.created_at)}  ·  last used {fmtDate(t.last_used_at)}
                  </div>
                </div>
                {!t.revoked && (
                  <button
                    onClick={() => revoke(t.pat_id, t.name)}
                    className="p-2 hover:bg-red-500/20 rounded text-slate-400 hover:text-red-300 flex-shrink-0"
                    title="Revoke"
                    data-testid={`revoke-btn-${t.pat_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* REST API v1 card */}
      <div className="mb-10 bg-[#0D1424] border border-cyan-400/20 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Terminal className="w-5 h-5 text-cyan-400" />
          <div className="font-display font-bold text-lg">REST API v1</div>
          <span className="mono-label text-[10px] text-cyan-300 border border-cyan-400/40 px-2 py-0.5">OPENAPI 3</span>
        </div>
        <p className="text-sm text-slate-300 mb-4">
          A stable, versioned REST surface for external assistants and integrations — estimates, clients, and the pricing book.
          Feed the OpenAPI URL below into your assistant's tool builder and it will self-configure.
          Auth uses the same PAT you mint above; select the right scopes.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <div className="mono-label text-slate-500 text-[11px] mb-1">BASE URL</div>
            <div className="bg-black/40 border border-white/10 p-2 font-mono text-sm flex items-center gap-2">
              <code className="text-cyan-300 flex-1 truncate">{backendUrl}/api/v1</code>
              <button onClick={() => copy("v1base", `${backendUrl}/api/v1`)} className="p-1 hover:bg-white/10 rounded flex-shrink-0" data-testid="copy-v1-base-btn">
                {copied === "v1base" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
              </button>
            </div>
          </div>
          <div>
            <div className="mono-label text-slate-500 text-[11px] mb-1">OPENAPI SPEC</div>
            <div className="bg-black/40 border border-white/10 p-2 font-mono text-sm flex items-center gap-2">
              <code className="text-cyan-300 flex-1 truncate">{backendUrl}/api/v1/openapi.json</code>
              <button onClick={() => copy("v1oapi", `${backendUrl}/api/v1/openapi.json`)} className="p-1 hover:bg-white/10 rounded flex-shrink-0" data-testid="copy-v1-openapi-btn">
                {copied === "v1oapi" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mb-4">
          <a href={`${backendUrl}/api/v1/docs`} target="_blank" rel="noreferrer" className="border border-cyan-400 text-cyan-300 hover:bg-cyan-400/10 mono-label text-xs px-4 py-2 flex items-center gap-2" data-testid="v1-docs-link">
            <ExternalLink className="w-3.5 h-3.5" />OPEN SWAGGER DOCS
          </a>
          <a href={`${backendUrl}/api/v1/redoc`} target="_blank" rel="noreferrer" className="border border-white/20 hover:border-cyan-400 text-slate-300 hover:text-cyan-300 mono-label text-xs px-4 py-2 flex items-center gap-2" data-testid="v1-redoc-link">
            <ExternalLink className="w-3.5 h-3.5" />OPEN REDOC
          </a>
        </div>
        <div className="text-xs text-slate-500 leading-relaxed">
          Endpoints: <code className="text-cyan-300">GET /estimates</code> · <code className="text-cyan-300">GET /estimates/{"{id}"}</code> · <code className="text-cyan-300">POST /estimates</code> · <code className="text-cyan-300">GET/POST /clients</code> · <code className="text-cyan-300">GET /pricing</code>.
          All money in <span className="text-white">integer cents</span>. ISO-8601 dates. Every estimate returns <code className="text-fuchsia-300">id</code> + shareable <code className="text-fuchsia-300">url</code>. Writes are rate-limited (60/min) and audit-logged per token.
        </div>
      </div>

      {/* Setup instructions */}
      <div className="mb-10 bg-[#0D1424] border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Terminal className="w-5 h-5 text-emerald-400" />
          <div className="font-display font-bold text-lg">Setup — Claude Desktop</div>
        </div>
        <ol className="list-decimal ml-5 text-sm text-slate-300 space-y-2 mb-4">
          <li>Open Claude Desktop → <span className="text-white">Settings</span> → <span className="text-white">Developer</span> → <span className="text-white">Edit Config</span>.</li>
          <li>Paste the JSON below into <code className="text-cyan-300 bg-black/40 px-1 py-0.5">claude_desktop_config.json</code> (merge with any existing <code className="text-cyan-300 bg-black/40 px-1 py-0.5">mcpServers</code>).</li>
          <li>Replace <code className="text-fuchsia-300 bg-black/40 px-1 py-0.5">{"<PASTE-YOUR-TOKEN-HERE>"}</code> with a token from above.</li>
          <li>Restart Claude Desktop. You'll see a <span className="text-white">🔨 tools</span> icon in the chat input — BidForge tools are ready.</li>
        </ol>
        <div className="relative bg-black/50 border border-white/10 p-4 font-mono text-xs text-slate-200 overflow-auto">
          <pre className="whitespace-pre-wrap break-all" data-testid="claude-config-json">{claudeConfig}</pre>
          <button
            onClick={() => copy("claude", claudeConfig)}
            className="absolute top-2 right-2 p-2 hover:bg-white/10 rounded"
            data-testid="copy-claude-config-btn"
          >
            {copied === "claude" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
          </button>
        </div>

        <div className="flex items-center gap-2 mt-8 mb-4">
          <Terminal className="w-5 h-5 text-sky-400" />
          <div className="font-display font-bold text-lg">Setup — Cursor / other HTTP MCP clients</div>
        </div>
        <div className="text-sm text-slate-300 mb-3">
          For clients that speak MCP over HTTP directly (Cursor, custom integrations), point at the hosted endpoint:
        </div>
        <div className="bg-black/40 border border-white/10 p-3 mb-3 font-mono text-sm flex items-center gap-2">
          <code className="text-cyan-300 flex-1 break-all" data-testid="mcp-endpoint-url">{mcpUrl}</code>
          <button onClick={() => copy("url", mcpUrl)} className="p-1 hover:bg-white/10 rounded">
            {copied === "url" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
          </button>
        </div>
        <div className="relative bg-black/50 border border-white/10 p-4 font-mono text-xs text-slate-200 overflow-auto">
          <pre className="whitespace-pre-wrap break-all" data-testid="cursor-config-json">{cursorConfig}</pre>
          <button
            onClick={() => copy("cursor", cursorConfig)}
            className="absolute top-2 right-2 p-2 hover:bg-white/10 rounded"
            data-testid="copy-cursor-config-btn"
          >
            {copied === "cursor" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
          </button>
        </div>

        <div className="flex items-center gap-2 mt-8 mb-4">
          <Terminal className="w-5 h-5 text-amber-400" />
          <div className="font-display font-bold text-lg">Setup — Jarvis / probe-based MCP clients</div>
        </div>
        <div className="text-sm text-slate-300 mb-2">
          Some custom MCP hosts (like Jarvis's <code className="text-cyan-300 bg-black/40 px-1 py-0.5">/tools_custom</code> probe) can only classify servers as "open" or "OAuth" — they can't pass a bearer token during the probe. For those, paste the URL below (token embedded as a query param).
        </div>
        <div className="text-xs text-amber-300 mb-3 border-l-2 border-amber-400 pl-2">
          ⚠ This URL contains your PAT — treat it like a password. Do NOT commit to source control, share, or paste into public logs. Use header auth (Claude Desktop / Cursor) whenever your client supports it.
        </div>
        <div className="relative bg-black/50 border border-amber-400/30 p-3 mb-1 font-mono text-xs">
          <code className="text-amber-200 break-all block pr-10" data-testid="jarvis-url">{jarvisUrl}</code>
          <button
            onClick={() => copy("jarvis", jarvisUrl)}
            className="absolute top-2 right-2 p-2 hover:bg-white/10 rounded disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="copy-jarvis-url-btn"
            disabled={!freshToken}
            title={freshToken ? "Copy" : "Generate a token above first"}
          >
            {copied === "jarvis" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className={`w-4 h-4 ${freshToken ? "text-amber-300" : "text-slate-600"}`} />}
          </button>
        </div>
        {!freshToken && (
          <div className="text-[11px] text-slate-500">Generate a token above to see your Jarvis URL. Existing tokens can't be re-shown — mint a fresh one for this URL.</div>
        )}
      </div>

      {/* Available tools */}
      <div className="mb-10 bg-[#0D1424] border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <div className="font-display font-bold text-lg">Available Tools ({tools.length})</div>
        </div>
        <div className="text-sm text-slate-400 mb-4">
          Every tool below is scoped to your workspace — the assistant can only see your opportunities, estimates, and past performance. Try asking things like:
        </div>
        <div className="grid gap-2 mb-6 text-sm text-slate-300">
          <div className="border-l-2 border-fuchsia-400/50 pl-3">"Show me my active pipeline — what's due this week?"</div>
          <div className="border-l-2 border-cyan-400/50 pl-3">"Search for roofing opportunities in TX under $500k."</div>
          <div className="border-l-2 border-emerald-400/50 pl-3">"Get the estimate for notice X and summarize the delta between Option A and B."</div>
          <div className="border-l-2 border-amber-400/50 pl-3">"Draft a proposal for notice Y using my Slate Creek template."</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tools-list">
          {tools.map((t) => (
            <div key={t.name} className="border border-white/10 bg-black/20 p-3">
              <div className="mono-label text-cyan-300 text-[11px] mb-1">{t.name}</div>
              <div className="text-xs text-slate-400 leading-relaxed">{t.description}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
